import React, { useState } from 'react';
import { usePropsContext } from '@uhg-abyss/web/hooks/usePropsContext';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Button } from '@uhg-abyss/web/ui/Button';
import { IconMaterial } from '@uhg-abyss/web/ui/IconMaterial';
import { IdCard } from '@sde/widgets-core/src/common/Image/IdCard';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { event } from '@uhg-abyss/web/tools/event';
import { usePayers } from '../../../../redux/resources/payers';
import { usercache } from '../../../../redux/tools/usercache';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Text } from '@uhg-abyss/web/ui/Text';

const StyledLabel = styled('label', {
  fontSize: '$md',
  color: '$gray8',
});
const StyledButtonMemberIdText = styled(Button, {
  marginTop: 16,
  marginBottom: 10,
});
const StyledDivChevronIcon = styled('div', {
  paddingTop: 2,
});
const StyledDivShowIdCard = styled('div', {
  marginTop: 5,
  maxHeight: 200,
  paddingLeft: 40,
  paddingRight: 40,
  overflow: 'hidden',
});
const StyledDivButtonBorder = styled('div', {
  marginTop: '3px',
  marginBottom: '6px',
  borderBottom: `3px solid $gray3`,
});
const Container = styled('div', {
  height: 'fit-content',
  width: '500px',
  padding: '10px',
});
const StyledButtonFullButtonWidth = styled(Button, {
  width: '100%',
});
const StyledSelectList = styled(SelectInput, {
  margin: '5px',
});

export const PayerSelect = () => {
  const { data, events } = usePropsContext();
  const { payer, payers } = data;
  const [isMemberIdCard, setMemberIdCard] = useState(false);
  const { setPayer } = usePayers.actions();
  const [selectedPayer, setSelectedPayer] = useState();

  const toggleMemberIdCard = () => {
    setMemberIdCard(!isMemberIdCard);
  };

  const handleSave = () => {
    setPayer(selectedPayer);
    usercache.set('payer', selectedPayer);
    events.onChange(selectedPayer);
    events.onSave(selectedPayer);
    event('CHANGE_PAYER_COMPLETION');
  };

  const handleReset = () => {
    setPayer(payer);
    usercache.set('payer', payer);
    events('onChange', payer);
  };

  const handleCancel = () => {
    handleReset();
    events('onCancel');
  };

  const handlePayerInfo = (payerId) => {
    let tempPayer;
    if (payerId) {
      tempPayer = payers.find((p) => p.id === payerId);
    }

    if (!tempPayer) {
      const [firstPayer] = payers;
      tempPayer = firstPayer;
    }

    const newSelectedPayer = tempPayer
      ? {
          payerId: tempPayer.id,
          payerName: tempPayer.name,
          payerLabel: tempPayer.label,
        }
      : null;

    setSelectedPayer(newSelectedPayer);
  };

  const handleChange = (payerId) => {
    handlePayerInfo(payerId);
  };

  return (
    <Container>
      <Text size='20px' fontWeight='bold' color='$primary1'>
        {data.title}
      </Text>
      <Divider />
      <div>
        <StyledLabel
          id='payer-select-label'
          htmlFor='payer-select'
          data-uhc-brand='font--semibold'
        >
          Payer Group
        </StyledLabel>
        <StyledSelectList
          id='payer-select'
          enableOutsideScroll
          aria-labelledby='payer-select-label'
          aria-describedby='payer-select-error'
          validators={{ required: (value) => !value }}
          disabled={payers.length === 1}
          options={payers}
          value={payer.payerId}
          valueKey='id'
          isLoading={false}
          placeholder='Select Payer...'
          onChange={handleChange}
          data-testid='payer-select'
        />
      </div>
      <StyledButtonMemberIdText
        variant='tertiary'
        onClick={toggleMemberIdCard}
        after={
          <StyledDivChevronIcon>
            {isMemberIdCard ? (
              <IconMaterial size={18} />
            ) : (
              <IconMaterial size={18} />
            )}
          </StyledDivChevronIcon>
        }
      >
        What is this?
      </StyledButtonMemberIdText>
      <If condition={isMemberIdCard}>
        <StyledDivShowIdCard>
          <IdCard alt='Member ID Card' />
        </StyledDivShowIdCard>
      </If>
      <StyledDivButtonBorder />
      <Layout.Group grow alignLayout='center'>
        <StyledButtonFullButtonWidth
          id='payer-select-cancel'
          variant='outline'
          onClick={handleCancel}
          aria-label='cancel payer selection'
        >
          <strong data-uhc-brand='font--semibold'>Cancel</strong>
        </StyledButtonFullButtonWidth>
        <StyledButtonFullButtonWidth
          id='payer-select-save'
          onClick={handleSave}
          aria-label='save payer selection'
        >
          <strong data-uhc-brand='font--semibold'>Save</strong>
        </StyledButtonFullButtonWidth>
      </Layout.Group>
    </Container>
  );
};
